import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";
import { Checkbox, VStack } from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { LandingPageConfigurationTable } from "../../components/LandingPageConfiguration/LandingPageConfigurationTable";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInput } from "../../hooks/useSearchInput";
import { useToast } from "../../hooks/useToast";
import { PlusIcon } from "../../styles/icons";

export const LandingPageConfigurations = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const { showErrorToast, showSuccessToast } = useToast();
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search landing page configurations...",
  });

  const {
    checked: showDeletedLandingPageConfiguration,
    onChange: onChangeDeletedLandingPageConfigurationCheck,
  } = useCheckboxState(false);

  const {
    items: landingPageConfigurations,
    refresh,
    isLoading,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getLandingPageConfigurations(business.BusinessId as string, {
        q: debouncedQuery,
        includeDeleted: showDeletedLandingPageConfiguration ? "true" : "false",
      }),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getLandingPageConfigurations(business.BusinessId as string, {
        q: debouncedQuery,
        includeDeleted: showDeletedLandingPageConfiguration ? "true" : "false",
        continuationToken,
      }),
    [business.BusinessId, debouncedQuery, showDeletedLandingPageConfiguration]
  );

  const deleteLandingPageConfigurationRequest = useAdminApiUpdateRequest(
    (apiClient, landingPageConfigurationId: string) =>
      apiClient.deleteLandingPageConfiguration(
        business.BusinessId!,
        landingPageConfigurationId
      ),
    [landingPageConfigurations]
  );

  const restoreLandingPageConfigurationRequest = useAdminApiUpdateRequest(
    (apiClient, landingPageConfigurationId: string) =>
      apiClient.restoreLandingPageConfiguration(
        business.BusinessId!,
        landingPageConfigurationId
      ),
    [landingPageConfigurations]
  );

  const onClickNewLandingPageConfiguration = () =>
    navigate(
      `/admin/businesses/${business.BusinessId}/landing-page-configurations/new`
    );

  const onClickEditLandingPageConfiguration = (
    landingPageConfiguration: CherryHubAdminApi.LandingPageConfigurationSummaryItem
  ) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/landing-page-configurations/${landingPageConfiguration.Meta?.Id}`
    );

  const onClickDeleteLandingPageConfiguration = useCallback(
    (row: CherryHubAdminApi.LandingPageConfigurationSummaryItem) =>
      showConfirmationDialog(
        "Delete Landing Page Configuration?",
        `Are you sure you wish to delete this landing page configuration: ${row.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteLandingPageConfigurationRequest.request(row.Meta?.Id!);
        }
      }),
    [landingPageConfigurations]
  );
  const onClickRestoreLandingPageConfiguration = useCallback(
    (row: CherryHubAdminApi.LandingPageConfigurationSummaryItem) =>
      showConfirmationDialog(
        "Restore Landing Page Configuration?",
        `Are you sure you wish to restore this deleted landing page configuration: ${row.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreLandingPageConfigurationRequest.request(row.Meta?.Id!);
        }
      }),
    [landingPageConfigurations]
  );

  useEffect(() => {
    if (restoreLandingPageConfigurationRequest.result) {
      if (restoreLandingPageConfigurationRequest.result.ok) {
        showSuccessToast("Landing page configuration successfully restored");
        refresh();
      } else {
        showErrorToast(
          restoreLandingPageConfigurationRequest.error ??
            "Failed to restore landing page configuration"
        );
      }
    }
  }, [restoreLandingPageConfigurationRequest.result, refresh]);

  useEffect(() => {
    if (deleteLandingPageConfigurationRequest.result) {
      if (deleteLandingPageConfigurationRequest.result.ok) {
        showSuccessToast("Landing page configuration successfully deleted");
        refresh();
      } else {
        showErrorToast(
          deleteLandingPageConfigurationRequest.error ??
            "Failed to delete landing page configuration"
        );
      }
    }
  }, [deleteLandingPageConfigurationRequest.result, refresh]);
  return (
    <>
      <PageHeading>
        <PageHeading.Title>Landing Page Configurations</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/landing-page-configurations`}
              >
                Landing Page Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeletedLandingPageConfiguration}
                onChange={onChangeDeletedLandingPageConfigurationCheck}
                title="Include deleted landing page configuration the list."
              >
                Include deleted
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={onClickNewLandingPageConfiguration}
              >
                New Landing Page Configuration
              </BusinessMenu>
            </Box>
          </HStack>
          <LandingPageConfigurationTable
            landingPageConfigurations={landingPageConfigurations}
            isLoading={isLoading}
            disabledActions={false}
            debouncedQuery={debouncedQuery}
            onClickDeleteLandingPageConfiguration={
              onClickDeleteLandingPageConfiguration
            }
            onClickEditLandingPageConfiguration={
              onClickEditLandingPageConfiguration
            }
            onClickRestoreLandingPageConfiguration={
              onClickRestoreLandingPageConfiguration
            }
          />
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
